import React, { useMemo, useState } from "react";

import { useClinicCall } from "../clinic-calling/ClinicCallProvider";
import { useAuthSelector } from "../../services/selectors/authSelector";
import * as Icons from "../../assets/svg/icons";

const compactList = (values) => values.filter((value) => value != null && String(value).trim() !== "");

const formatAppointmentTime = (value) => {
  const displayValue = value?.display_time || value?.display_date_time || value?.date_and_time;
  return displayValue || "Time not set";
};

const normalizeManualPhone = (value) => String(value || "").trim().replace(/[^+\d]/g, "");

const getClinicName = (appointment, contact) => appointment?.clinic?.name || contact?.clinic_name || "Clinic";

const getDoctorLabel = (appointment, contact) => (
  appointment?.doctor_display_name ||
  appointment?.doctor?.display_name ||
  appointment?.doctor?.name ||
  contact?.doctor_display_name ||
  contact?.doctor_name ||
  ""
);

const getClinicPhone = (appointment, contact) => contact?.clinic_phone || appointment?.clinic?.phone || null;

const getPatientName = (patient, appointmentDelivery) => (
  appointmentDelivery?.pharmacy_patient?.name ||
  compactList([patient?.first_name, patient?.last_name]).join(" ") ||
  "Patient"
);

const DriverAppointmentCallPanel = ({ patient, appointmentDelivery }) => {
  const { pharmacyId } = useAuthSelector();
  const { call, isCallActive, startClinicCall } = useClinicCall();
  const [manualPhone, setManualPhone] = useState("");
  const [manualPhoneError, setManualPhoneError] = useState("");
  const [showManualPhone, setShowManualPhone] = useState(false);

  const appointments = useMemo(() => {
    const source = appointmentDelivery?.appointments;
    return Array.isArray(source) ? source : [];
  }, [appointmentDelivery]);

  if (appointments.length === 0) return null;

  const appointment = appointments[0];
  const contact = appointmentDelivery?.clinic_contact || {};
  const appointmentId = appointment?.id;
  const clinicName = getClinicName(appointment, contact);
  const clinicPhone = getClinicPhone(appointment, contact);
  const doctorLabel = getDoctorLabel(appointment, contact);
  const appointmentTime = formatAppointmentTime(appointment);
  const isThisCallActive =
    isCallActive &&
    call?.appointmentId != null &&
    appointmentId != null &&
    String(call.appointmentId) === String(appointmentId);
  const callDisabled = !appointmentId || (isCallActive && !isThisCallActive);

  const startCall = async (manualToPhone = "") => {
    if (!appointmentId) return;

    await startClinicCall({
      pharmacyId,
      appointmentId,
      pharmacyPatientId: patient?.id,
      manualToPhone,
      // Number to dial natively if the masked SIP line can't be established.
      fallbackPhone: manualToPhone || clinicPhone || "",
      clinicName,
      patientName: getPatientName(patient, appointmentDelivery),
    });
  };

  const handleCallClick = () => {
    if (callDisabled) return;

    if (clinicPhone) {
      startCall();
      return;
    }

    setManualPhone("");
    setManualPhoneError("");
    setShowManualPhone(true);
  };

  const closeManualPhoneModal = () => {
    setShowManualPhone(false);
    setManualPhone("");
    setManualPhoneError("");
  };

  const submitManualPhone = async () => {
    const normalizedPhone = normalizeManualPhone(manualPhone);
    if (normalizedPhone.replace(/\D/g, "").length < 7) {
      setManualPhoneError("Enter a valid clinic phone number.");
      return;
    }

    closeManualPhoneModal();
    await startCall(normalizedPhone);
  };

  return (
    <>
      <div className="mt-2 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">
              Appointment today
            </p>
            <p className="truncate text-sm font-semibold text-gray-900">
              {appointmentTime} with {clinicName}
            </p>
            <p className="truncate text-xs text-gray-500">
              {doctorLabel || "Clinic appointment"}
              {appointments.length > 1 ? ` +${appointments.length - 1} more` : ""}
            </p>
          </div>
          <button
            type="button"
            disabled={callDisabled}
            onClick={handleCallClick}
            className={`flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-md px-3 text-xs font-bold ${
              callDisabled ? "bg-gray-300 text-gray-500" : "bg-[#10b981] text-white"
            }`}
          >
            <Icons.PhoneIcon color={callDisabled ? "#6b7280" : "#ffffff"} />
            {isThisCallActive ? "Calling" : clinicPhone ? "Call Clinic" : "Enter Phone"}
          </button>
        </div>
      </div>

      {showManualPhone ? (
        <div
          className="fixed inset-0 z-[22000] flex items-end justify-center bg-slate-950/60 px-3 pb-3 sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`route-manual-clinic-phone-${patient?.id || "patient"}`}
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl">
            <h2 id={`route-manual-clinic-phone-${patient?.id || "patient"}`} className="text-lg font-bold text-slate-950">
              Clinic phone
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Enter the clinic number for this call only.
            </p>
            <input
              type="tel"
              inputMode="tel"
              value={manualPhone}
              onChange={(event) => {
                setManualPhone(event.target.value);
                setManualPhoneError("");
              }}
              placeholder="Clinic phone number"
              className="mt-4 h-12 w-full rounded-xl border border-slate-300 px-3 text-base font-semibold text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
            {manualPhoneError ? (
              <p className="mt-2 text-sm font-semibold text-red-600">{manualPhoneError}</p>
            ) : null}
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={closeManualPhoneModal}
                className="h-11 flex-1 rounded-xl border border-slate-200 bg-white text-sm font-bold text-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitManualPhone}
                className="h-11 flex-1 rounded-xl bg-[#10b981] text-sm font-bold text-white"
              >
                Start Call
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default DriverAppointmentCallPanel;
